/**
 * HealthPlanBlock — Rich structured rendering for health & fitness specialist data.
 * Receives raw JSON from the brain's specialist_data CUSTOM event and renders
 * interactive UI components instead of relying on LLM-regenerated markdown.
 *
 * Design inspiration: wger (weekly structure), MyFitnessPal (macro tables),
 * Hevy/Strong (exercise cards), Notion (structured blocks/callouts).
 */

import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@flowstate/utils';
import {
  ChevronDown,
  ChevronRight,
  UtensilsCrossed,
  Dumbbell,
  Activity,
  Pill,
  TrendingUp,
  BarChart3,
  AlertTriangle,
  ShoppingCart,
  Lightbulb,
  Trophy,
} from 'lucide-react';

// ─── Types ──────────────────────────────────────────────────────────

interface HealthPlanBlockProps {
  tool: string;
  data: unknown;
}

// Data shapes from health specialist edge functions (all fields optional for defensive access)
interface PlanFood { name?: string; portion?: string; calories?: number; protein?: number; carbs?: number; fat?: number }
interface PlanMeal { name?: string; time?: string; foods?: PlanFood[]; notes?: string }
interface DayMealPlan { meals?: PlanMeal[]; dailyTotals?: { calories?: number; protein?: number; carbs?: number; fat?: number }; notes?: string }
interface PlanExercise { name?: string; sets?: number; reps?: string; rest?: string; rpe?: number; notes?: string }
interface DayWorkoutPlan { name?: string; focus?: string; warmup?: string[]; exercises?: PlanExercise[]; cooldown?: string[]; notes?: string }
type ShoppingItem = string | { name?: string; quantity?: string };
interface NutritionPlanData {
  planName?: string; dailyCalories?: number;
  macroSplit?: { protein?: number; carbs?: number; fat?: number };
  weeklyPlan?: Record<string, DayMealPlan>;
  shoppingList?: Record<string, ShoppingItem[]>;
  mealPrepTips?: string[]; guidelines?: string[]; warnings?: string[];
}
interface WorkoutPlanData {
  planName?: string; experienceLevel?: string; daysPerWeek?: number; splitType?: string;
  weeklySchedule?: Record<string, DayWorkoutPlan>;
  progressionPlan?: string; deloadStrategy?: string; recoveryTips?: string[]; warnings?: string[];
}
interface MacroTrackerPlanData {
  foods?: PlanFood[]; mealTotals?: { calories?: number; protein?: number; carbs?: number; fat?: number };
  macroRatio?: { proteinPct?: number; carbsPct?: number; fatPct?: number };
  remainingBudget?: { calories?: number; protein?: number; carbs?: number; fat?: number };
  analysis?: string; suggestions?: string[]; warnings?: string[];
}
interface FitnessAssessmentPlanData {
  overallScore?: number;
  goals?: Record<string, Array<string | { description?: string; goal?: string }>>;
  recommendations?: Array<string | { text?: string; description?: string }>;
  benchmarks?: Array<{ metric?: string; name?: string; current?: string; target?: string; status?: string }>;
  warnings?: string[];
}
interface SupplementPlanData {
  recommendations?: Array<{ name?: string; supplement?: string; dosage?: string; timing?: string; purpose?: string }>;
  interactions?: string[]; stackSuggestion?: string;
  budgetBreakdown?: { budget?: string; essential?: string[]; nice?: string[] };
  mythBusters?: string[]; warnings?: string[];
}
interface ProgressPlanData {
  progressScore?: number;
  trends?: Array<{ metric?: string; direction?: string; details?: string }>;
  compliance?: { training?: number; nutrition?: number; sleep?: number };
  plateauAnalysis?: string; adjustments?: Record<string, string[]>;
  wins?: string[]; motivationalInsight?: string; warnings?: string[];
}

// ─── Shared Components ──────────────────────────────────────────────

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
const DAY_LABELS: Record<string, string> = {
  monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday',
  thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday',
};

/** Collapsible day section with animated chevron */
function DaySection({ day: _day, label, children, defaultOpen = false }: {
  day: string;
  label: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger
        className="flex items-center gap-2 w-full py-2 px-3 rounded-md hover:bg-muted/50 transition-colors text-left"
        aria-label={`Toggle ${label}`}
      >
        {open
          ? <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
          : <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />}
        <span className="font-medium text-sm">{label}</span>
      </CollapsibleTrigger>
      <CollapsibleContent className="pl-6 pr-2 pb-2">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
}

/** Compact data table matching EnhancedMarkdownRenderer's table style */
function DataTable({ headers, rows }: { headers: string[]; rows: (string | number)[][] }) {
  if (!rows.length) { return null; }
  return (
    <div className="my-2 overflow-x-auto rounded-lg border border-border/50">
      <table className="w-full border-collapse text-sm">
        <thead className="bg-muted/50 border-b border-border/50">
          <tr>
            {headers.map((h, i) => (
              <th key={i} className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri} className="hover:bg-muted/30 transition-colors">
              {row.map((cell, ci) => (
                <td key={ci} className="px-3 py-2 border-t border-border/30 text-sm">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Stat badge row for plan overview */
function StatBadges({ items }: { items: { label: string; value: string | number }[] }) {
  return (
    <div className="flex flex-wrap gap-2 my-2">
      {items.map((item, i) => (
        <Badge key={i} variant="secondary" className="text-xs font-normal">
          <span className="font-semibold mr-1">{item.label}:</span> {item.value}
        </Badge>
      ))}
    </div>
  );
}

/** Warning/tip callout list */
function CalloutList({ items, variant = 'warning' }: { items: string[]; variant?: 'warning' | 'tip' | 'win' }) {
  if (!items?.length) { return null; }
  const styles = {
    warning: { icon: <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />, bg: 'bg-amber-500/10 border-amber-500/30' },
    tip: { icon: <Lightbulb className="h-4 w-4 text-blue-500 shrink-0 mt-0.5" />, bg: 'bg-blue-500/10 border-blue-500/30' },
    win: { icon: <Trophy className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />, bg: 'bg-green-500/10 border-green-500/30' },
  };
  const s = styles[variant];
  return (
    <div className={cn('rounded-lg p-3 border my-2', s.bg)}>
      <ul className="space-y-1.5">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2 text-sm">
            {s.icon}
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Macro ratio bar (protein / carbs / fat) */
function MacroBar({ protein, carbs, fat }: { protein: number; carbs: number; fat: number }) {
  const total = protein + carbs + fat;
  if (total === 0) { return null; }
  const pPct = Math.round((protein / total) * 100);
  const cPct = Math.round((carbs / total) * 100);
  const fPct = 100 - pPct - cPct;

  return (
    <div className="my-2">
      <div className="flex h-3 rounded-full overflow-hidden border border-border/50">
        <div className="bg-blue-500" style={{ width: `${pPct}%` }} title={`Protein ${pPct}%`} />
        <div className="bg-amber-500" style={{ width: `${cPct}%` }} title={`Carbs ${cPct}%`} />
        <div className="bg-rose-400" style={{ width: `${fPct}%` }} title={`Fat ${fPct}%`} />
      </div>
      <div className="flex justify-between text-xs text-muted-foreground mt-1">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500 inline-block" /> Protein {pPct}%</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500 inline-block" /> Carbs {cPct}%</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-rose-400 inline-block" /> Fat {fPct}%</span>
      </div>
    </div>
  );
}

// ─── Nutrition Plan ─────────────────────────────────────────────────

function NutritionPlanBlock({ data }: { data: NutritionPlanData }) {
  const planName = data?.planName ?? 'Nutrition Plan';
  const dailyCalories = data?.dailyCalories ?? 0;
  const macroSplit = data?.macroSplit ?? {};
  const weeklyPlan = data?.weeklyPlan ?? {};
  const shoppingList = data?.shoppingList;
  const mealPrepTips = data?.mealPrepTips as string[] | undefined;
  const guidelines = data?.guidelines as string[] | undefined;
  const warnings = data?.warnings as string[] | undefined;

  return (
    <Card className="glass-card border-border/40 overflow-hidden">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <UtensilsCrossed className="h-5 w-5 text-primary" />
          {planName}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <StatBadges items={[
          { label: 'Daily Calories', value: dailyCalories },
          { label: 'Protein', value: `${macroSplit.protein ?? 0}g` },
          { label: 'Carbs', value: `${macroSplit.carbs ?? 0}g` },
          { label: 'Fat', value: `${macroSplit.fat ?? 0}g` },
        ]} />
        {macroSplit.protein && <MacroBar protein={macroSplit.protein} carbs={macroSplit.carbs ?? 0} fat={macroSplit.fat ?? 0} />}

        <Separator />

        {/* Per-day meal plans */}
        {DAYS.map((day, idx) => {
          const dayData = weeklyPlan[day];
          if (!dayData) { return null; }
          const meals = dayData.meals ?? [];
          const totals = dayData.dailyTotals ?? {};
          return (
            <DaySection key={day} day={day} label={`${DAY_LABELS[day]}`} defaultOpen={idx === 0}>
              <DataTable
                headers={['Meal', 'Time', 'Foods', 'Cal', 'P', 'C', 'F']}
                rows={meals.map((m) => [
                  m.name ?? '',
                  m.time ?? '',
                  (m.foods ?? []).map((f) => `${f.name} (${f.portion})`).join(', '),
                  (m.foods ?? []).reduce((s: number, f) => s + (f.calories ?? 0), 0),
                  (m.foods ?? []).reduce((s: number, f) => s + (f.protein ?? 0), 0) + 'g',
                  (m.foods ?? []).reduce((s: number, f) => s + (f.carbs ?? 0), 0) + 'g',
                  (m.foods ?? []).reduce((s: number, f) => s + (f.fat ?? 0), 0) + 'g',
                ])}
              />
              {totals.calories && (
                <div className="text-xs text-muted-foreground mt-1 pl-1">
                  Daily total: {totals.calories} cal · {totals.protein ?? 0}g P · {totals.carbs ?? 0}g C · {totals.fat ?? 0}g F
                </div>
              )}
            </DaySection>
          );
        })}

        {/* Shopping list */}
        {shoppingList && (
          <>
            <Separator />
            <div className="mt-2">
              <h4 className="text-sm font-semibold flex items-center gap-1.5 mb-2">
                <ShoppingCart className="h-4 w-4" /> Shopping List
              </h4>
              {Object.entries(shoppingList).map(([category, items]) => (
                <div key={category} className="mb-2">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{category}</p>
                  <ul className="text-sm list-disc list-inside pl-1">
                    {items.map((item, i) => (
                      <li key={i}>{typeof item === 'string' ? item : `${item.name} — ${item.quantity}`}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </>
        )}

        <CalloutList items={mealPrepTips ?? []} variant="tip" />
        <CalloutList items={guidelines ?? []} variant="tip" />
        <CalloutList items={warnings ?? []} variant="warning" />
      </CardContent>
    </Card>
  );
}

// ─── Workout Plan ───────────────────────────────────────────────────

function WorkoutPlanBlock({ data }: { data: WorkoutPlanData }) {
  const planName = data?.planName ?? 'Workout Plan';
  const experienceLevel = data?.experienceLevel ?? '';
  const daysPerWeek = data?.daysPerWeek ?? 0;
  const splitType = data?.splitType ?? '';
  const weeklySchedule = data?.weeklySchedule ?? {};
  const progressionPlan = data?.progressionPlan as string | undefined;
  const deloadStrategy = data?.deloadStrategy as string | undefined;
  const recoveryTips = data?.recoveryTips as string[] | undefined;
  const warnings = data?.warnings as string[] | undefined;

  return (
    <Card className="glass-card border-border/40 overflow-hidden">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Dumbbell className="h-5 w-5 text-primary" />
          {planName}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <StatBadges items={[
          ...(experienceLevel ? [{ label: 'Level', value: experienceLevel }] : []),
          ...(daysPerWeek ? [{ label: 'Days/Week', value: daysPerWeek }] : []),
          ...(splitType ? [{ label: 'Split', value: splitType }] : []),
        ]} />

        <Separator />

        {DAYS.map((day, idx) => {
          const dayData = weeklySchedule[day];
          if (!dayData) { return null; }
          const exercises = dayData.exercises ?? [];
          const warmup = dayData.warmup ?? [];
          const cooldown = dayData.cooldown ?? [];
          const focus = dayData.focus ?? '';

          return (
            <DaySection key={day} day={day} label={`${DAY_LABELS[day]}${focus ? ` — ${focus}` : ''}`} defaultOpen={idx === 0}>
              {warmup.length > 0 && (
                <div className="text-xs text-muted-foreground mb-1">
                  <span className="font-medium">Warmup:</span> {warmup.join(' → ')}
                </div>
              )}
              <DataTable
                headers={['Exercise', 'Sets × Reps', 'Rest', 'RPE', 'Notes']}
                rows={exercises.map((e) => [
                  e.name ?? '',
                  `${e.sets ?? ''}×${e.reps ?? ''}`,
                  e.rest ?? '',
                  e.rpe ?? '—',
                  e.notes ?? '',
                ])}
              />
              {cooldown.length > 0 && (
                <div className="text-xs text-muted-foreground mt-1">
                  <span className="font-medium">Cooldown:</span> {cooldown.join(' → ')}
                </div>
              )}
            </DaySection>
          );
        })}

        {(progressionPlan || deloadStrategy) && (
          <>
            <Separator />
            {progressionPlan && (
              <div className="text-sm">
                <span className="font-medium">📈 Progression:</span> {progressionPlan}
              </div>
            )}
            {deloadStrategy && (
              <div className="text-sm">
                <span className="font-medium">🔄 Deload:</span> {deloadStrategy}
              </div>
            )}
          </>
        )}

        <CalloutList items={recoveryTips ?? []} variant="tip" />
        <CalloutList items={warnings ?? []} variant="warning" />
      </CardContent>
    </Card>
  );
}

// ─── Macro Tracker ──────────────────────────────────────────────────

function MacroTrackerBlock({ data }: { data: MacroTrackerPlanData }) {
  const foods = data?.foods ?? [];
  const mealTotals = data?.mealTotals ?? {};
  const macroRatio = data?.macroRatio ?? {};
  const remainingBudget = data?.remainingBudget;
  const analysis = data?.analysis as string | undefined;
  const suggestions = data?.suggestions as string[] | undefined;
  const warnings = data?.warnings as string[] | undefined;

  return (
    <Card className="glass-card border-border/40 overflow-hidden">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <BarChart3 className="h-5 w-5 text-primary" />
          Macro Tracker
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <DataTable
          headers={['Food', 'Portion', 'Cal', 'P', 'C', 'F']}
          rows={foods.map((f) => [
            f.name ?? '', f.portion ?? '', f.calories ?? 0,
            `${f.protein ?? 0}g`, `${f.carbs ?? 0}g`, `${f.fat ?? 0}g`,
          ])}
        />

        {mealTotals.calories != null && (
          <div className="bg-muted/40 rounded-lg p-3 text-sm">
            <span className="font-semibold">Totals:</span> {mealTotals.calories} cal · {mealTotals.protein ?? 0}g P · {mealTotals.carbs ?? 0}g C · {mealTotals.fat ?? 0}g F
          </div>
        )}

        {(macroRatio.proteinPct || macroRatio.carbsPct || macroRatio.fatPct) && (
          <MacroBar protein={macroRatio.proteinPct ?? 0} carbs={macroRatio.carbsPct ?? 0} fat={macroRatio.fatPct ?? 0} />
        )}

        {remainingBudget && (
          <div className="text-sm text-muted-foreground">
            <span className="font-medium">Remaining:</span> {remainingBudget.calories ?? 0} cal · {remainingBudget.protein ?? 0}g P · {remainingBudget.carbs ?? 0}g C · {remainingBudget.fat ?? 0}g F
          </div>
        )}

        {analysis && <p className="text-sm">{analysis}</p>}
        <CalloutList items={suggestions ?? []} variant="tip" />
        <CalloutList items={warnings ?? []} variant="warning" />
      </CardContent>
    </Card>
  );
}

// ─── Fitness Assessment ─────────────────────────────────────────────

function FitnessAssessmentBlock({ data }: { data: FitnessAssessmentPlanData }) {
  const overallScore = data?.overallScore ?? 0;
  const goals = data?.goals ?? {};
  const recommendations = data?.recommendations ?? [];
  const benchmarks = data?.benchmarks ?? [];
  const warnings = data?.warnings as string[] | undefined;

  return (
    <Card className="glass-card border-border/40 overflow-hidden">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Activity className="h-5 w-5 text-primary" />
          Fitness Assessment
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Score display */}
        <div className="flex items-center gap-3">
          <div className={cn(
            'text-3xl font-bold rounded-xl w-16 h-16 flex items-center justify-center',
            overallScore >= 80 ? 'bg-green-500/15 text-green-600' :
            overallScore >= 60 ? 'bg-amber-500/15 text-amber-600' :
            'bg-red-500/15 text-red-600',
          )}>
            {overallScore}
          </div>
          <div className="text-sm text-muted-foreground">Overall fitness score out of 100</div>
        </div>

        {/* Goals */}
        {['shortTerm', 'mediumTerm', 'longTerm'].map(tier => {
          const tierGoals = goals[tier] ?? [];
          if (!tierGoals.length) { return null; }
          const tierLabels: Record<string, string> = { shortTerm: '🎯 Short-term', mediumTerm: '📅 Medium-term', longTerm: '🏆 Long-term' };
          return (
            <div key={tier}>
              <p className="text-sm font-medium">{tierLabels[tier]}</p>
              <ul className="list-disc list-inside text-sm pl-1 space-y-0.5">
                {tierGoals.map((g, i) => (
                  <li key={i}>{typeof g === 'string' ? g : g.description ?? g.goal ?? JSON.stringify(g)}</li>
                ))}
              </ul>
            </div>
          );
        })}

        {/* Recommendations */}
        {recommendations.length > 0 && (
          <>
            <Separator />
            <CalloutList
              items={recommendations.map((r) => typeof r === 'string' ? r : r.text ?? r.description ?? JSON.stringify(r))}
              variant="tip"
            />
          </>
        )}

        {/* Benchmarks */}
        {benchmarks.length > 0 && (
          <DataTable
            headers={['Metric', 'Current', 'Target', 'Status']}
            rows={benchmarks.map((b) => [
              b.metric ?? b.name ?? '', b.current ?? '', b.target ?? '', b.status ?? '',
            ])}
          />
        )}

        <CalloutList items={warnings ?? []} variant="warning" />
      </CardContent>
    </Card>
  );
}

// ─── Supplement Guide ───────────────────────────────────────────────

function SupplementGuideBlock({ data }: { data: SupplementPlanData }) {
  const recommendations = data?.recommendations ?? [];
  const interactions = data?.interactions ?? [];
  const stackSuggestion = data?.stackSuggestion as string | undefined;
  const budgetBreakdown = data?.budgetBreakdown;
  const mythBusters = data?.mythBusters as string[] | undefined;
  const warnings = data?.warnings as string[] | undefined;

  return (
    <Card className="glass-card border-border/40 overflow-hidden">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Pill className="h-5 w-5 text-primary" />
          Supplement Guide
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {recommendations.length > 0 && (
          <DataTable
            headers={['Supplement', 'Dosage', 'Timing', 'Purpose']}
            rows={recommendations.map((r) => [
              r.name ?? r.supplement ?? '',
              r.dosage ?? '',
              r.timing ?? '',
              r.purpose ?? '',
            ])}
          />
        )}

        {stackSuggestion && (
          <div className="text-sm bg-muted/40 rounded-lg p-3">
            <span className="font-semibold">💊 Stack:</span> {stackSuggestion}
          </div>
        )}

        {budgetBreakdown && (
          <div className="text-sm">
            <p className="font-medium mb-1">💰 Budget: {budgetBreakdown.budget ?? ''}</p>
            {budgetBreakdown.essential && budgetBreakdown.essential.length > 0 && (
              <p><span className="font-medium">Essential:</span> {budgetBreakdown.essential.join(', ')}</p>
            )}
            {budgetBreakdown.nice && budgetBreakdown.nice.length > 0 && (
              <p><span className="font-medium">Nice to have:</span> {budgetBreakdown.nice.join(', ')}</p>
            )}
          </div>
        )}

        <CalloutList items={interactions} variant="warning" />
        <CalloutList items={mythBusters ?? []} variant="tip" />
        <CalloutList items={warnings ?? []} variant="warning" />
      </CardContent>
    </Card>
  );
}

// ─── Progress Review ────────────────────────────────────────────────

function ProgressReviewBlock({ data }: { data: ProgressPlanData }) {
  const progressScore = data?.progressScore ?? 0;
  const trends = data?.trends ?? [];
  const compliance = data?.compliance ?? {};
  const plateauAnalysis = data?.plateauAnalysis as string | undefined;
  const adjustments = data?.adjustments ?? {};
  const wins = data?.wins ?? [];
  const motivationalInsight = data?.motivationalInsight as string | undefined;
  const warnings = data?.warnings as string[] | undefined;

  return (
    <Card className="glass-card border-border/40 overflow-hidden">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <TrendingUp className="h-5 w-5 text-primary" />
          Progress Review
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Score + Compliance */}
        <div className="flex items-center gap-4 flex-wrap">
          <div className={cn(
            'text-3xl font-bold rounded-xl w-16 h-16 flex items-center justify-center',
            progressScore >= 80 ? 'bg-green-500/15 text-green-600' :
            progressScore >= 60 ? 'bg-amber-500/15 text-amber-600' :
            'bg-red-500/15 text-red-600',
          )}>
            {progressScore}
          </div>
          <div className="flex-1 space-y-1.5 min-w-[150px]">
            {compliance.training != null && (
              <div className="flex items-center gap-2 text-sm">
                <span className="w-20 text-muted-foreground">Training</span>
                <Progress value={compliance.training} className="h-2 flex-1" />
                <span className="text-xs w-10 text-right">{compliance.training}%</span>
              </div>
            )}
            {compliance.nutrition != null && (
              <div className="flex items-center gap-2 text-sm">
                <span className="w-20 text-muted-foreground">Nutrition</span>
                <Progress value={compliance.nutrition} className="h-2 flex-1" />
                <span className="text-xs w-10 text-right">{compliance.nutrition}%</span>
              </div>
            )}
            {compliance.sleep != null && (
              <div className="flex items-center gap-2 text-sm">
                <span className="w-20 text-muted-foreground">Sleep</span>
                <Progress value={compliance.sleep} className="h-2 flex-1" />
                <span className="text-xs w-10 text-right">{compliance.sleep}%</span>
              </div>
            )}
          </div>
        </div>

        {/* Trends */}
        {trends.length > 0 && (
          <>
            <Separator />
            <div className="space-y-1">
              {trends.map((t, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  <span>{t.direction === 'up' ? '📈' : t.direction === 'down' ? '📉' : '➡️'}</span>
                  <span className="font-medium">{t.metric}:</span>
                  <span className="text-muted-foreground">{t.details}</span>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Plateau */}
        {plateauAnalysis && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-sm">
            <span className="font-semibold">⚠️ Plateau Analysis:</span> {plateauAnalysis}
          </div>
        )}

        {/* Adjustments */}
        {Object.keys(adjustments).length > 0 && (
          <>
            <Separator />
            {['training', 'nutrition', 'recovery'].map(key => {
              const items = adjustments[key] ?? [];
              if (!items.length) { return null; }
              const icons: Record<string, string> = { training: '🏋️', nutrition: '🍽️', recovery: '😴' };
              return (
                <div key={key}>
                  <p className="text-sm font-medium">{icons[key] ?? ''} {key.charAt(0).toUpperCase() + key.slice(1)} adjustments</p>
                  <ul className="list-disc list-inside text-sm pl-1 space-y-0.5">
                    {items.map((item: string, i: number) => <li key={i}>{item}</li>)}
                  </ul>
                </div>
              );
            })}
          </>
        )}

        <CalloutList items={wins} variant="win" />

        {/* Motivational quote */}
        {motivationalInsight && (
          <blockquote className="border-l-4 border-primary/50 pl-3 py-1 text-sm italic text-muted-foreground">
            {motivationalInsight}
          </blockquote>
        )}

        <CalloutList items={warnings ?? []} variant="warning" />
      </CardContent>
    </Card>
  );
}

// ─── Main Dispatcher ────────────────────────────────────────────────

export function HealthPlanBlock({ tool, data }: HealthPlanBlockProps) {
  switch (tool) {
    case 'nutrition-plan':
      return <NutritionPlanBlock data={data as NutritionPlanData} />;
    case 'workout-plan':
      return <WorkoutPlanBlock data={data as WorkoutPlanData} />;
    case 'macro-tracker':
      return <MacroTrackerBlock data={data as MacroTrackerPlanData} />;
    case 'fitness-assessment':
      return <FitnessAssessmentBlock data={data as FitnessAssessmentPlanData} />;
    case 'supplement-guide':
      return <SupplementGuideBlock data={data as SupplementPlanData} />;
    case 'progress-review':
      return <ProgressReviewBlock data={data as ProgressPlanData} />;
    default:
      return null;
  }
}
